import React from 'react';

import Icon, { faEllipsis } from '~components/icon';
import { Row as TableRow } from '~components/table-view';

/**
 * Splices the `ExportDialog.tsx` ellipsis break row between a head slice and a tail slice of `rows`,
 * shared by both the copied-list (`BudgetSampleList`) and the not-copied-list (`FailureList`) so the
 * break-splicing logic exists in exactly one place (TAV-3258 Decision 1).
 *
 * `rows` is expected to already be in "head then tail" order, as sent by the API. The break row is only
 * inserted when `total` is bigger than `assessed`, the count of ids the sample actually covers. `assessed`
 * defaults to `firstCount + lastCount`, which is correct for a caller whose `rows` population IS the
 * population `total` counts (e.g. `FailureList`, where both describe the not-copied set). It is wrong for
 * a caller like `BudgetSampleList` at the bulk-copy Preview step: there, `rows` (and `firstCount`/
 * `lastCount`) describe only the COPYABLE subset of the sample, while `total` counts the whole selection,
 * so `firstCount + lastCount` under-counts how much of the selection was actually looked at and produces a
 * break (and a "showing first/last of N" caption) even when every selected budget is on screen somewhere
 * (TAV-3258 review). Passing the true assessed count (the API's `sample_size`) fixes that without changing
 * this helper's contract for callers that don't need it.
 */
export const buildSampleTableRows = <T extends { id?: unknown }>(
  rows: T[],
  firstCount: number,
  lastCount: number,
  total: number,
  assessed: number = firstCount + lastCount,
): (T | TableRow)[] => {
  // Guard the shared helper against a non-finite count regardless of caller discipline: a NaN comparison
  // fails open (`total <= NaN` is always false), and `rows.slice(0, undefined)` / `rows.slice(undefined)`
  // both return the WHOLE array, so an unguarded non-finite count would duplicate every row with a break
  // spliced in between the two copies.
  if (
    !Number.isFinite(firstCount) ||
    !Number.isFinite(lastCount) ||
    !Number.isFinite(total) ||
    !Number.isFinite(assessed)
  ) {
    return rows;
  }

  if (total <= assessed) return rows;

  const first = rows.slice(0, firstCount);
  const last = rows.slice(firstCount);

  return [
    ...first,
    {
      id: 'sample-break',
      isCustom: true,
      getCustomRow: ({ id, colCount }: { id: string | number; colCount: number; rowCount: number }) =>
        React.createElement(
          'tr',
          { key: id },
          React.createElement(
            'td',
            { colSpan: colCount },
            React.createElement(
              'div',
              { className: 'text-center' },
              React.createElement(Icon, { icon: faEllipsis, className: 'pt-2 pb-2' }),
            ),
          ),
        ),
    },
    ...last,
  ];
};
